<template>
  <!-- The home page of the new navigation (LIQ-2643, LimeIQ Design System): a brand banner and
       one tile per main menu section. A tile lists that section's menu options - the same items,
       filtered by the same roles, as the menu rail (see useNavMenu). -->
  <div class="liq-home">
    <section class="liq-home__hero" aria-label="LimeIQ">
      <canvas ref="kvRef" class="liq-home__kv" aria-hidden="true"></canvas>
      <div class="liq-home__hero-inner">
        <h1 class="liq-home__logo"><LimeiqLogo /></h1>
        <p class="liq-home__mission">Transforming data <em>with precision.</em></p>
      </div>
    </section>

    <div class="liq-home__grid">
      <section
        v-for="tile in tiles"
        :key="tile.key"
        class="liq-home__tile"
        :class="{ 'is-open': openTile === tile.key }"
        :aria-label="tile.label"
        @keydown.esc="closeTile(tile.key)"
      >
        <button
          :ref="`face-${tile.key}`"
          type="button"
          class="liq-home__face"
          :aria-expanded="String(openTile === tile.key)"
          :aria-controls="`liq-home-opts-${tile.key}`"
          :tabindex="openTile === tile.key ? -1 : 0"
          @click="showTile(tile.key)"
        >
          <span class="liq-home__tile-icon">
            <b-icon :icon="tile.icon" aria-hidden="true"></b-icon>
          </span>
          <span class="liq-home__tile-title">{{ tile.label }}</span>
        </button>

        <transition name="liq-home-fade">
          <div
            v-if="openTile === tile.key"
            :id="`liq-home-opts-${tile.key}`"
            class="liq-home__opts"
          >
            <div class="liq-home__opts-head">
              <span>{{ tile.label }}</span>
              <button
                :ref="`close-${tile.key}`"
                type="button"
                class="liq-home__close"
                aria-label="Close"
                @click="closeTile(tile.key)"
              >
                <b-icon icon="x" scale="1.3" aria-hidden="true"></b-icon>
              </button>
            </div>

            <div class="liq-home__opts-list">
              <template v-for="option in tile.children">
                <a
                  v-if="option.href"
                  :key="option.key"
                  :href="option.href"
                  :target="option.external ? '_blank' : null"
                  :rel="option.external ? 'noopener noreferrer' : null"
                  class="liq-home__opt"
                >
                  <span>{{ option.label }}</span>
                  <b-icon icon="chevron-right" aria-hidden="true"></b-icon>
                </a>
                <button
                  v-else-if="!option.route"
                  :key="option.key"
                  type="button"
                  class="liq-home__opt"
                  :disabled="isDisabled(option)"
                  @click="onAction(option)"
                >
                  <span>{{ option.label }}</span>
                  <b-icon
                    :icon="option.animate && option.animate(context) ? 'arrow-clockwise' : 'chevron-right'"
                    :animation="option.animate && option.animate(context) ? 'spin' : ''"
                    aria-hidden="true"
                  ></b-icon>
                </button>
                <router-link
                  v-else
                  :key="option.key"
                  :to="option.route"
                  class="liq-home__opt"
                  @click.native="onLeaf(option)"
                >
                  <span>{{ option.label }}</span>
                  <b-icon
                    v-if="option.badge && option.badge(context)"
                    icon="exclamation-triangle-fill"
                    class="text-warning"
                    aria-label="Needs attention"
                  ></b-icon>
                  <b-icon v-else icon="chevron-right" aria-hidden="true"></b-icon>
                </router-link>
              </template>
            </div>
          </div>
        </transition>
      </section>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { computed, defineComponent } from '@vue/composition-api'

import LimeiqLogo from '@components/LimeiqLogo'
import { useNavMenu } from '@composables/useNavMenu'
import { HOME_TILE_KEYS, isItemDisabled } from '@/config/navMenu'

// Deep cyan to mint - the banner's pixel mosaic (after the brand's green01 artwork).
const KEY_VISUAL_TONES = ['#2a4445', '#355a59', '#4f8b80', '#6fc2a7', '#90ffcf']
const KEY_VISUAL_ROW = 14

// A template ref inside v-for is an array in Vue 2.
const firstRef = (value) => (Array.isArray(value) ? value[0] : value)

export default defineComponent({
  name: 'HomePage',
  components: { LimeiqLogo },
  setup() {
    const { context, menu } = useNavMenu()

    const tiles = computed(() =>
      HOME_TILE_KEYS.map((key) => menu.value.find((item) => item.key === key)).filter(
        (item) => item && item.children
      )
    )

    return { context, tiles }
  },
  data() {
    return {
      // One tile open at a time.
      openTile: null
    }
  },
  mounted() {
    this.resizeTimer = null
    this.drawKeyVisual()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    clearTimeout(this.resizeTimer)
  },
  methods: {
    async showTile(key) {
      this.openTile = key
      await this.$nextTick()
      const close = firstRef(this.$refs[`close-${key}`])
      if (close) close.focus()
    },
    async closeTile(key) {
      if (this.openTile !== key) return
      this.openTile = null
      await this.$nextTick()
      const face = firstRef(this.$refs[`face-${key}`])
      if (face) face.focus()
    },
    onAction(option) {
      Vue.prototype.$emitter.emit('nav-action', option.action)
      this.openTile = null
    },
    onLeaf(option) {
      if (option.action) Vue.prototype.$emitter.emit('nav-action', option.action)
    },
    isDisabled(option) {
      return isItemDisabled(option, this.context)
    },
    onResize() {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(this.drawKeyVisual, 120)
    },
    // A static mosaic of quantised blocks, brighter and more mint towards the right, then faded
    // into the banner behind the text. Seeded, so it draws the same every time - no animation.
    drawKeyVisual() {
      const canvas = this.$refs.kvRef
      if (!canvas) return

      const rect = canvas.getBoundingClientRect()
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr

      const g = canvas.getContext('2d')
      g.setTransform(dpr, 0, 0, dpr, 0, 0)
      g.clearRect(0, 0, rect.width, rect.height)

      let seed = 7
      const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647

      for (let y = 0; y < rect.height; y += KEY_VISUAL_ROW) {
        let x = 0
        while (x < rect.width) {
          const w = KEY_VISUAL_ROW + Math.floor(rnd() * 4) * KEY_VISUAL_ROW
          const t = x / rect.width
          const wave = 0.5 + 0.5 * Math.sin(x * 0.012 - y * 0.035 + Math.sin(y * 0.02) * 1.6)
          const v = Math.min(0.999, Math.max(0, t * 0.75 + wave * 0.45 + (rnd() - 0.5) * 0.35 - 0.25))
          g.globalAlpha = 0.18 + 0.62 * t
          g.fillStyle = KEY_VISUAL_TONES[Math.floor(v * KEY_VISUAL_TONES.length)]
          g.fillRect(x, y, w - 1, KEY_VISUAL_ROW - 1)
          x += w
        }
      }

      g.globalAlpha = 1
      const fade = g.createLinearGradient(0, 0, rect.width * 0.55, 0)
      fade.addColorStop(0, 'rgba(32, 52, 53, 1)')
      fade.addColorStop(1, 'rgba(32, 52, 53, 0)')
      g.fillStyle = fade
      g.fillRect(0, 0, rect.width * 0.55, rect.height)
    }
  }
})
</script>

<style lang="scss" scoped>
// LimeIQ Design System - see assets/sass/custom/_brand-tokens.scss.
.liq-home {
  padding: 24px;
  color: var(--liq-ink);
  font-family: var(--liq-font-sans);

  &__hero {
    position: relative;
    display: flex;
    align-items: center;
    min-height: 260px;
    margin-bottom: 24px;
    padding: 48px 52px 52px;
    overflow: hidden;
    border-radius: 24px;
    background: var(--liq-deep);
    color: var(--liq-on-deep);
  }

  &__kv {
    position: absolute;
    top: 0;
    right: 0;
    width: 62%;
    height: 100%;
    pointer-events: none;
  }

  &__hero-inner {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 22px;
    max-width: 640px;
  }

  &__logo {
    height: clamp(52px, 6.5vw, 92px);
    margin: 0;
    color: var(--liq-mint);
    line-height: 0;
  }

  &__mission {
    margin: 0;
    color: var(--liq-on-deep);
    font-family: var(--liq-font-serif);
    font-size: clamp(22px, 2.4vw, 30px);
    font-weight: 400;
    line-height: 1.15;

    em {
      color: var(--liq-mint);
      font-style: normal;
    }
  }

  // All tiles in one row on wide screens, however many the user can see.
  &__grid {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(0, 1fr);
    gap: 18px;
  }

  &__tile {
    position: relative;
    height: 336px;
    overflow: hidden;
    border: 1px solid var(--liq-hair);
    border-radius: 12px;
    background: var(--liq-paper);
    box-shadow: 0 1px 2px rgba(32, 52, 53, 0.06), 0 6px 20px rgba(32, 52, 53, 0.07);
    transition: transform 0.2s var(--liq-ease), box-shadow 0.2s var(--liq-ease);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 2px 4px rgba(32, 52, 53, 0.08), 0 12px 28px rgba(32, 52, 53, 0.12);
    }
  }

  &__face {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 20px;
    width: 100%;
    padding: 20px;
    border: 0;
    background: transparent;
    cursor: pointer;
    transition: opacity 0.2s var(--liq-ease);

    &:focus {
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid var(--liq-deep);
      outline-offset: -4px;
      border-radius: 12px;
    }

    .is-open & {
      opacity: 0;
    }
  }

  &__tile-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 84px;
    height: 84px;
    border-radius: 22.5%;
    background: var(--liq-deep);
    color: var(--liq-mint);
    font-size: 38px;
  }

  &__tile-title {
    color: var(--liq-ink);
    font-size: 19px;
    font-weight: 500;
  }

  &__opts {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    padding: 18px 16px;
    background: var(--liq-deep);
    color: var(--liq-on-deep);
  }

  &__opts-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    color: var(--liq-mint);
    font-size: 16px;
    font-weight: 500;
  }

  &__close {
    display: flex;
    padding: 2px;
    border: 0;
    border-radius: 6px;
    background: none;
    color: var(--liq-on-deep);
    cursor: pointer;

    &:hover {
      color: var(--liq-mint);
    }

    &:focus {
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid var(--liq-mint);
    }
  }

  &__opts-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    overflow-y: auto;
  }

  // Mint hover with deep cyan text - never black or white text on mint.
  &__opt {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 32px;
    padding: 0 12px;
    border: 1px solid var(--liq-hair-mint);
    border-radius: var(--liq-radius-sm);
    background: transparent;
    color: var(--liq-on-deep);
    font-size: 14px;
    font-weight: 500;
    text-align: left;
    text-decoration: none;
    cursor: pointer;
    transition: background 0.15s var(--liq-ease), color 0.15s var(--liq-ease),
      border-color 0.15s var(--liq-ease);

    &:hover {
      border-color: var(--liq-mint);
      background: var(--liq-mint);
      color: var(--liq-deep);
      text-decoration: none;
    }

    &:focus {
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid var(--liq-mint);
      outline-offset: 1px;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  @media (max-width: 1300px) {
    &__grid {
      grid-auto-flow: row;
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  @media (max-width: 960px) {
    &__grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    &__hero {
      padding: 36px 28px;
    }

    &__kv {
      width: 100%;
      opacity: 0.45;
    }
  }

  @media (max-width: 640px) {
    padding: 16px;

    &__grid {
      grid-template-columns: 1fr;
    }
  }
}

.liq-home-fade-enter-active,
.liq-home-fade-leave-active {
  transition: opacity 0.2s var(--liq-ease);
}
.liq-home-fade-enter,
.liq-home-fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .liq-home__tile,
  .liq-home__face,
  .liq-home__opt,
  .liq-home-fade-enter-active,
  .liq-home-fade-leave-active {
    transition: none;
  }

  .liq-home__tile:hover {
    transform: none;
  }
}
</style>
